import React from 'react';
import { Stethoscope, Award, Users, Target, Heart, TrendingUp } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const PhysiciansAdvanced = () => {
  const benefits = [
    {
      icon: Award,
      title: 'Board-Certified Excellence',
      description: 'Access to highly qualified board-certified physicians across multiple specialties.',
    },
    {
      icon: Users,
      title: 'Large Candidate Pool',
      description: 'Draw from our extensive network of skilled physicians and advanced practitioners.',
    },
    {
      icon: Target,
      title: 'Tailored Matches',
      description: 'Customized search process to find the perfect fit for your facility.',
    },
    {
      icon: Heart,
      title: 'Patient-Centered',
      description: 'Physicians committed to delivering exceptional patient care and outcomes.',
    },
  ];

  const features = [
    {
      title: 'Customized',
      description: 'We provide solutions that suit your requirements at best.',
    },
    {
      title: 'Together, We are One',
      description: 'We intend to be Your Strategic Partner and Adviser.',
    },
    {
      title: 'Growth & Expansion',
      description: 'We scale your requirements.',
    },
  ];

  return (
    <div className="physicians-advanced-page">
      <PageMeta
        title="Physicians & Advanced Practice"
        description="Board-certified physicians, nurse practitioners, and physician assistants. Tailored staffing for your facility from BluekraftMed."
        path="/physicians-advanced"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Stethoscope className="w-20 h-20 mx-auto mb-6" />
            <h1 className="text-5xl font-bold mb-6">Physician and Advanced Practitioners</h1>
            <p className="text-xl leading-relaxed">
              Connect with top physicians and advanced practice providers
            </p>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=800&h=600&fit=crop"
                alt="Physician"
                className="rounded-lg shadow-xl"
              />
            </div>
            <div>
              <p className="text-cyan-600 font-semibold mb-2">WHY US?</p>
              <div className="w-16 h-1 bg-cyan-600 mb-6"></div>
              <h2 className="text-4xl font-bold mb-6">Our Best Physicians?</h2>
              <p className="text-xl text-gray-600 mb-4">The Best know, the Test!</p>
              <p className="text-gray-600 text-lg leading-relaxed">
                Your Partnership with us isn't just about being compatible and cost effective. It's also about being "Trustworthy" and staying "ahead of the curve" by providing you Physicians and Advanced Practitioners that meet the current market demands!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Choose Our Physicians</h2>
            <p className="text-gray-600 text-lg">Excellence in medical expertise and patient care</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center">
                <CardHeader>
                  <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-cyan-600" />
                  </div>
                  <CardTitle>{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Patient Care Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <p className="text-cyan-600 font-semibold mb-2">WHY US?</p>
              <div className="w-16 h-1 bg-cyan-600 mb-6"></div>
              <h2 className="text-4xl font-bold mb-6">
                Patient Care is Vital and we provide the hands that make it happen!
              </h2>
              <p className="text-xl text-gray-600 mb-4">Meet our team members and our work places</p>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                As we draw from our large pool of candidates, our team focuses on the specific skills and expertise required for long-term success.
              </p>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                We tailor each search to the specific needs of your practice or facility, and we work with you to find the best physician candidate.
              </p>
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-6 text-lg">
                Find Your Physician
              </Button>
            </div>
            <div className="order-1 md:order-2">
              <img
                src="https://images.unsplash.com/photo-1638202993928-7267aad84c31?w=800&h=600&fit=crop"
                alt="Healthcare Team"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="text-2xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-lg">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <img
              src="https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=1200&h=600&fit=crop"
              alt="Medical Team"
              className="rounded-lg shadow-xl w-full"
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Find Your Perfect Physician?</h2>
            <p className="text-xl mb-8">
              Contact us today to discuss your physician staffing needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-white text-cyan-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Contact Us
              </Button>
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-cyan-600 px-8 py-6 text-lg">
                Call: +1 (469)-466-2313
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PhysiciansAdvanced;